/** SUNTRONIX ONE — Inline Sticky Notes attached to a record.
 *
 *  Usage:
 *    <RecordStickyNotes contextType="order" contextId={order.id} />
 *
 *  Shows only notes attached to this record. Supports personal + shared
 *  visibility, colours, pin, delete. Reuses `/api/sticky-notes` scoped
 *  by context_type + context_id.
 */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { StickyNote, Plus, Pin, Trash2, Lock, Users2 } from "lucide-react";

const COLORS = ["yellow", "blue", "green", "pink", "purple", "orange"];
const COLOR_BG = {
  yellow: "bg-yellow-100 border-yellow-300",
  blue:   "bg-blue-100 border-blue-300",
  green:  "bg-emerald-100 border-emerald-300",
  pink:   "bg-pink-100 border-pink-300",
  purple: "bg-purple-100 border-purple-300",
  orange: "bg-orange-100 border-orange-300",
};


export default function RecordStickyNotes({ contextType, contextId,
                                             masterCompanyId, title = "Notes" }) {
  const { user } = useAuth();
  const [notes, setNotes] = useState([]);
  const [busy, setBusy] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [draftBody, setDraftBody] = useState("");
  const [draftColor, setDraftColor] = useState("yellow");
  const [draftShared, setDraftShared] = useState(true);

  const load = useCallback(async () => {
    if (!contextType || !contextId) return;
    setBusy(true);
    try {
      const { data } = await api.get("/sticky-notes", {
        params: { scope: "all", context_type: contextType, context_id: contextId },
      });
      setNotes(data.items || []);
    } catch { /* silent */ }
    finally { setBusy(false); }
  }, [contextType, contextId]);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!draftBody.trim()) return;
    try {
      await api.post("/sticky-notes", {
        body: draftBody,
        color: draftColor,
        visibility: draftShared ? "shared" : "private",
        master_company_id: draftShared
          ? (masterCompanyId || user?.master_company_id || null)
          : null,
        context_type: contextType,
        context_id: contextId,
      });
      setDrafting(false); setDraftBody(""); setDraftColor("yellow");
      toast.success("Note attached to record");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const togglePin = async (n) => {
    try { await api.put(`/sticky-notes/${n.id}`, { pinned: !n.pinned }); load(); }
    catch { toast.error("Pin failed"); }
  };
  const del = async (n) => {
    if (!window.confirm("Delete this note?")) return;
    try { await api.delete(`/sticky-notes/${n.id}`); load(); }
    catch { toast.error("Delete failed"); }
  };
  const cycleColor = async (n) => {
    const next = COLORS[(COLORS.indexOf(n.color) + 1) % COLORS.length];
    try { await api.put(`/sticky-notes/${n.id}`, { color: next }); load(); }
    catch { /* ignore */ }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-md p-4 space-y-3"
      data-testid={`record-sticky-${contextType}-${contextId}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <StickyNote className="h-4 w-4 text-slate-600" strokeWidth={1.6} />
          <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-600">
            {title}
          </div>
          {notes.length > 0 && (
            <span className="text-[10px] text-slate-400">· {notes.length}</span>
          )}
        </div>
        {!drafting && (
          <Button size="sm" variant="outline" className="h-7"
            onClick={() => setDrafting(true)}
            data-testid={`record-sticky-add-${contextType}`}>
            <Plus className="h-3 w-3 mr-1" /> Add note
          </Button>
        )}
      </div>

      {drafting && (
        <div className={"border rounded-md p-2.5 " + (COLOR_BG[draftColor] || COLOR_BG.yellow)}
          data-testid="record-sticky-draft">
          <textarea autoFocus value={draftBody}
            onChange={(e) => setDraftBody(e.target.value)}
            placeholder={`Note for this ${contextType}…`}
            className="w-full h-20 text-[13px] bg-transparent resize-none focus:outline-none" />
          <div className="flex items-center justify-between mt-1 gap-2 flex-wrap">
            <div className="flex items-center gap-2">
              <div className="flex gap-1">
                {COLORS.map((c) => (
                  <button key={c} onClick={() => setDraftColor(c)}
                    className={"h-4 w-4 rounded-full border " +
                      COLOR_BG[c] + (draftColor === c ? " ring-2 ring-slate-900" : "")} />
                ))}
              </div>
              <div className="text-[10px] text-slate-500">|</div>
              <button onClick={() => setDraftShared(!draftShared)}
                className={"h-6 px-2 rounded text-[10px] font-medium uppercase tracking-wider inline-flex items-center gap-1 " +
                  (draftShared
                    ? "bg-slate-900 text-white"
                    : "bg-slate-200 text-slate-700")}
                data-testid="record-sticky-visibility-toggle">
                {draftShared ? <Users2 className="h-3 w-3" /> : <Lock className="h-3 w-3" />}
                {draftShared ? "Shared" : "Personal"}
              </button>
            </div>
            <div className="flex gap-1">
              <Button size="sm" variant="ghost" className="h-7 text-slate-500"
                onClick={() => { setDrafting(false); setDraftBody(""); }}>
                Cancel
              </Button>
              <Button size="sm" onClick={create} disabled={!draftBody.trim()}
                className="h-7 bg-slate-900 text-white hover:bg-slate-800"
                data-testid="record-sticky-save">
                Save
              </Button>
            </div>
          </div>
        </div>
      )}

      {busy && notes.length === 0 ? (
        <div className="text-center text-[12px] text-slate-500 py-4">Loading…</div>
      ) : notes.length === 0 && !drafting ? (
        <div className="text-center text-[12px] text-slate-400 py-4">
          No notes yet. Add one to keep everyone in sync.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {notes.map((n) => (
            <div key={n.id}
              data-testid={`record-sticky-note-${n.id}`}
              className={"border rounded-md p-2.5 relative group " +
                (COLOR_BG[n.color] || COLOR_BG.yellow)}>
              <div className="text-[13px] text-slate-800 whitespace-pre-wrap break-words">
                {n.body}
              </div>
              <div className="flex items-center justify-between mt-2 pt-1.5 border-t border-black/5">
                <div className="text-[10px] text-slate-500 inline-flex items-center gap-1">
                  {n.visibility === "shared"
                    ? <Users2 className="h-2.5 w-2.5" />
                    : <Lock className="h-2.5 w-2.5" />}
                  {n.owner_name || ""} · {(n.updated_at || "").slice(0, 10)}
                </div>
                <div className="flex gap-0.5 opacity-70 group-hover:opacity-100">
                  <button onClick={() => cycleColor(n)}
                    className="h-6 w-6 rounded hover:bg-black/10 grid place-items-center"
                    title="Change colour">
                    <div className={"h-3 w-3 rounded-full border " + (COLOR_BG[n.color] || COLOR_BG.yellow)} />
                  </button>
                  <button onClick={() => togglePin(n)}
                    className={"h-6 w-6 rounded hover:bg-black/10 grid place-items-center " +
                      (n.pinned ? "text-slate-900" : "text-slate-400")}
                    title={n.pinned ? "Unpin" : "Pin"}>
                    <Pin className="h-3 w-3" />
                  </button>
                  <button onClick={() => del(n)}
                    className="h-6 w-6 rounded hover:bg-black/10 grid place-items-center text-slate-400 hover:text-rose-700"
                    title="Delete">
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
